import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import ContactUserSection from '../components/ContactUserSection';
import { formatDate, formatDateTime, formatCurrency } from '../utils/format';
import { useForm, Controller } from 'react-hook-form';
import { calculateTotalAmount, calculateItemSubtotal, calculateSellingPriceIncludingTax } from '../utils/function';
import TableView from '../components/TableView';
import LabelValueItem from '../components/LabelValueItem';
import OrderClientInfo from '../components/OrderClientInfo';
import useOrderTable from '../hooks/useOrderTable';
import { getEstimateStatusOptions } from '../utils/estimateStatus';
import {
  Grid, Box, Typography, Button, TextField, InputAdornment, Tooltip,
  Stack, Link, Divider, Paper, IconButton, Alert, MenuItem
} from '@mui/material';
import { Add, SupervisorAccount, Delete, Sync, HelpOutline, Edit, Close, Check, Refresh } from '@mui/icons-material';
import PdfPrintButton from '../components/PdfPrintButton';
const EstimateDetailPage = ({ hasLimit, userRole }) => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({
    items: []
  });
  const [status, setStatus] = useState("");
  const statusOptions = getEstimateStatusOptions({
    hasLimit,
    userRole,
  });
  const isApprovalPending =
    String(orderData?.estimate_status_id) === '2';

  const canUpdateStatus =
    !isApprovalPending ||
    ['admin', 'manager'].includes(userRole);
  const { register, handleSubmit, control, trigger, getValues, formState: { errors }, setValue, reset } = useForm({
    defaultValues: orderData,
  });
  const handleItemDelete = async (index, estimate_item_id) => {
    const values = getValues();

    // 新規行（バリデーション・confirmなし）
    if (!estimate_item_id) {
      const updatedItems = values.items.filter((_, i) => i !== index);

      const { total_amount, total_amount_including_tax } =
        calculateTotalAmount(updatedItems);

      setOrderData(prev => ({
        ...prev,
        items: updatedItems,
        total_amount,
        total_amount_including_tax,
      }));

      setValue('items', updatedItems);

      return;
    }
    // バリデーション実行
    const valid = await trigger(); // 全フィールドをチェック
    if (!valid) {
      showSnackbar('入力内容に不備があります。削除できません', 'error');
      return;
    }

    const ok = window.confirm(
      'この明細を削除します。よろしいですか？\n\n' +
      '※ 見積を取り消す場合は「見積キャンセル」ボタンを使用してください。'
    );
    if (!ok) return;

    try {

      // 削除対象を除いた items 配列を作る
      const updatedItems = values.items.filter(item => item.estimate_item_id !== estimate_item_id);
      // 合計計算
      const { total_amount, total_amount_including_tax } = calculateTotalAmount(updatedItems);

      // サーバー送信用データ
      const submitData = {
        ...orderData,
        items: updatedItems,
        total_amount,
        total_amount_including_tax,
        delete_item_id: estimate_item_id,
      };

      // DB 更新
      const res = await axiosInstance.put(`/estimate/${id}/update`, { orderData: submitData });

      showSnackbar(res?.data?.message || '見積アイテムを削除しました', 'success');

      fetchEstimate();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      showSnackbar(err?.response?.data?.message || 'この見積は確定済みのため、削除できません', 'error');
    }
  };
  const {
    columns,
    updateItem,
    handleItemChange,
  } = useOrderTable({
    control,
    errors,
    isEditMode,
    hasLimit,
    axiosInstance,
    selectedClient,
    orderData,
    setOrderData,
    calculateSellingPriceIncludingTax,
    calculateItemSubtotal,
    calculateTotalAmount,
    formatCurrency,
    handleItemDelete,
  });
  const fetchEstimate = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/estimate/${id}`);
      setOrderData(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      setError(err?.response?.data?.message || '取得に失敗しました');
      setOrderData(null);
    }
  }, [id, showSnackbar]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  useEffect(() => {
    if (!orderData) return;

    const formattedItems = (orderData.items || []).map(item => ({
      ...item,
      price: Math.floor(item.price),          // 定価を整数化
      tax_rate: Math.floor(item.tax_rate),    // 税率も整数
    }));

    reset({
      ...orderData,
      items: formattedItems,
      valid_until: orderData.valid_until
        ? formatDate(orderData.valid_until)
        : '',
    });
  }, [orderData, reset]);

  useEffect(() => {
    const snackbarState = location.state?.snackbar;
    if (snackbarState) {
      showSnackbar(snackbarState.message, snackbarState.severity);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, showSnackbar]);
  useEffect(() => {
    if (orderData?.estimate_status_id) {
      setStatus(String(orderData.estimate_status_id));
    }
  }, [orderData?.estimate_status_id]);

  const addItem = () => {
    const newItem = {
      product_id: null,
      variant_id: null,
      sku: '',
      price: 0,
      quantity: 1,
      selling_price: 0,
      selling_price_including_tax: 0,
      tax_rate: 0,
      subtotal: 0,
      subtotal_including_tax: 0,
    };

    const newItems = [...orderData.items, newItem];

    setOrderData({
      ...orderData,
      items: newItems,
    });
    setValue(`items.${newItems.length - 1}`, newItem);
  };
  if (!orderData) return null;
  const updateStatus = async (statusId) => {
    const ok = window.confirm('ステータスを変更します。\nこの操作は取り消せません。よろしいですか？');
    if (!ok) return;

    try {
      const res = await axiosInstance.put(`/estimate/${id}/update-status`, {
        status_id: statusId,
      });

      fetchEstimate();
      showSnackbar(res?.data?.message || '見積情報が更新されました', 'success');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    }
  };
  const onSubmit = async (data) => {
    const submitData = {
      ...orderData,
      ...data,
      estimate_date: null,
      items: orderData.items,
      total_amount: orderData.total_amount,
      total_amount_including_tax: orderData.total_amount_including_tax
    };
    try {
      const res = await axiosInstance.put(`/estimate/${id}/update`, { orderData: submitData });
      showSnackbar(res?.data?.message || '見積情報が更新されました', 'success');
      fetchEstimate();
      setIsEditMode(false);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    }
  };

  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant="h1" component="h1">見積もり</Typography>
      </Stack>
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Paper variant='section'>
          <Alert
            severity={
              orderData.estimate_status_id === '5'
                ? 'success'
                : orderData.estimate_status_id === '6'
                  ? 'error'
                  : 'info'
            }
            sx={{ mb: 2 }}
          >
            {!isEditMode &&
              !['5', '6'].includes(String(orderData?.estimate_status_id)) ? (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <TextField
                  select
                  size="small"
                  label="ステータス"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={!canUpdateStatus}
                  sx={{ width: 220, }}
                >
                  {statusOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="contained"
                  onClick={() => updateStatus(Number(status))}
                  disabled={
                    !canUpdateStatus ||
                    !status ||
                    status === String(orderData?.estimate_status_id)
                  }
                >
                  更新
                </Button>
              </Stack>
            ) : (
              orderData.status_name
            )}
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><LabelValueItem label='見積日時' value={formatDateTime(orderData.estimate_date)} /></Grid>
            <Grid item xs={12} sm={6}><LabelValueItem label='見積番号' value={orderData.display_estimate_number} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography component='h2' variant="h4" mb={2}>お客様情報</Typography>
            {hasLimit('max_clients') && orderData.client_id && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SupervisorAccount />}
                onClick={() => navigate(`/clients/${orderData.client_id}`)}
              >
                顧客情報を確認
              </Button>
            )}
          </Stack>
          <OrderClientInfo
            hasLimit={hasLimit}
            isEditMode={isEditMode}
            register={register}
            control={control}
            setValue={setValue}
            errors={errors}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            mode='estimate'
            setSelectedClient={setSelectedClient}
          />
          <Divider sx={{ my: 2 }} />
          <TextField
            label="備考"
            multiline
            minRows={3}
            disabled={!isEditMode}
            error={!!errors.estimate_notes}
            helperText={errors.estimate_notes?.message}
            {...register('estimate_notes', {
              maxLength: {
                value: 2000,
                message: '2000文字以内で入力してください',
              },
            })}
            inputProps={{ maxLength: 2000 }}
            sx={{ mt: 2 }}
          />
          <Divider sx={{ my: 2 }} />
          <Typography component='h2' variant="h4" sx={{ mb: 0 }}>商品情報</Typography>
          <TableView dataset={orderData.items ?? []} columns={columns} />
          <Button
            type="button"
            variant="outlined"
            startIcon={<Add />}
            onClick={addItem}
            disabled={!isEditMode}
            size='small'
            sx={{ mt: 1 }}
          >
            アイテムを追加
          </Button>
          {/* 合計金額 */}
          <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight="bold">合計金額（税抜）</Typography>
                <Typography fontWeight="bold">{formatCurrency(orderData.total_amount)}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography fontWeight="bold">合計金額（税込）</Typography>
                <Typography fontSize={22} fontWeight="bold">{formatCurrency(orderData.total_amount_including_tax)}</Typography>
              </Stack>
              <Divider />
            </Stack>
          </Box>
          <Divider sx={{ my: 2 }} />
          <ContactUserSection
            title="見積担当者"
            isEditMode={isEditMode}
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
          />
          <div className='button-row'>
            {['1'].includes(orderData.estimate_status_id) && (
              <>
                <Button
                  variant='outlined'
                  startIcon={isEditMode ? <Close /> : <Edit />}
                  onClick={async () => {
                    if (isEditMode) {
                      await fetchEstimate();
                    }
                    setIsEditMode(prev => !prev);
                  }}
                >
                  {isEditMode ? '編集を破棄する' : 'この見積を編集'}
                </Button>
                {isEditMode &&
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Sync />}
                  >
                    内容を更新する
                  </Button>
                }
              </>
            )}
          </div>
          <Divider sx={{ my: 4 }} />
          <Alert severity="info" icon={<HelpOutline />}>
            帳票に記載される自社情報は「会社設定」で設定できます。
            <Link
              component="button"
              onClick={() => navigate('/setting/invoice')}
              underline="none"
              sx={{ ml: 2 }}
            >
              （会社設定）
            </Link>
          </Alert>
          <div className='button-row'>
            {hasLimit('invoices_and_delivery') &&
              <PdfPrintButton
                id={orderData.estimate_id}
                isEditMode={isEditMode}
                pdfType="estimate" label="見積書"
              />
            }
            {!isEditMode &&
              <Button
                variant="outlined"
                color="primary"
                sx={{ ml: 1 }}
                size='small'
                onClick={async () => {
                  try {
                    const res = await axiosInstance.post(`/estimate/${id}/version`);
                    navigate(`/estimates/${res.data.estimateId}`, {
                      state: { snackbar: { message: res?.data?.message || '見積をコピーして再作成しました。', severity: 'success' } }
                    });
                  } catch (err) {
                    if (process.env.NODE_ENV === 'development') {
                      console.error(err);
                    }
                    showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
                  }
                }}
              >
                コピーして再作成
              </Button>
            }
          </div>
        </Paper >
      </form >
    </>
  );
};

export default EstimateDetailPage;
